/**
 * Tiny markdown-link parser for system message bodies.
 *
 * System messages (vitrine lead → admin, approval requests, etc.)
 * frequently want to point the recipient at a deep URL — "abre em
 * /vitrine para aprovar ou rejeitar." Rendering that as plain text
 * costs the user a copy/paste; rendering it as a real `<a>` is the
 * difference between a notification that *informs* and one that
 * *moves you forward*.
 *
 * We deliberately avoid pulling in a full markdown library (would
 * cost ~30 KB gzipped to render fewer than 5 features). The parser
 * supports exactly two things:
 *
 *   • `[text](url)` — explicit markdown link
 *   • bare `https://…` URLs — auto-linked
 *
 * Anything else is rendered verbatim. The same parser is reused in
 * the inbox preview (where the result is collapsed back to plain
 * text — the user doesn't need a clickable preview, they need to
 * see the link's display text instead of `[text](url)` noise).
 */

import { Fragment } from 'react';

/** A parsed body segment. `text` is plain content; `link` carries
 *  the rendered display text and the href to navigate to. */
export type BodySegment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; href: string };

const MD_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const BARE_URL = /\bhttps?:\/\/[^\s)]+/g;

/**
 * Parse a body string into a flat array of text + link segments.
 *
 * The two regexes run in two passes so a markdown link's URL part
 * (which contains `https://...`) doesn't ALSO get matched by the
 * bare-URL pass. Markdown links win when they overlap.
 */
export function parseMessageBody(body: string): BodySegment[] {
  if (!body) return [];

  const out: BodySegment[] = [];

  // Pass 1 — markdown links. Slice the input into [text...md-link...]
  // segments by walking the regex matches in order.
  let cursor = 0;
  const mdMatches: Array<{ start: number; end: number; text: string; href: string }> = [];
  for (const m of body.matchAll(MD_LINK)) {
    if (m.index === undefined) continue;
    mdMatches.push({
      start: m.index,
      end: m.index + m[0].length,
      text: m[1] ?? '',
      href: m[2] ?? '',
    });
  }

  // Walk through markdown matches; for each plain-text gap between
  // them, run pass 2 (bare URL detection).
  for (const md of mdMatches) {
    if (md.start > cursor) {
      pushWithBareLinks(out, body.slice(cursor, md.start));
    }
    out.push({ kind: 'link', text: md.text, href: md.href });
    cursor = md.end;
  }
  if (cursor < body.length) {
    pushWithBareLinks(out, body.slice(cursor));
  }

  return out;
}

/** Helper — split a chunk on bare URLs and push the segments. */
function pushWithBareLinks(out: BodySegment[], chunk: string): void {
  let cursor = 0;
  for (const m of chunk.matchAll(BARE_URL)) {
    if (m.index === undefined) continue;
    if (m.index > cursor) {
      out.push({ kind: 'text', text: chunk.slice(cursor, m.index) });
    }
    const url = m[0];
    out.push({ kind: 'link', text: url, href: url });
    cursor = m.index + url.length;
  }
  if (cursor < chunk.length) {
    out.push({ kind: 'text', text: chunk.slice(cursor) });
  }
}

/**
 * Collapse a parsed body back into plain text — used by the inbox
 * preview where the row is single-line and a hyperlink wouldn't be
 * clickable inside the preview anyway. Returns the link's display
 * text rather than `[text](url)` noise.
 */
export function messageBodyPlain(body: string): string {
  return parseMessageBody(body)
    .map((seg) => seg.text)
    .join('');
}

/**
 * Render a parsed body inline as React nodes. Links open in the
 * same tab when they're internal (start with `/` or share the
 * current origin) and in a new tab otherwise.
 */
export function MessageBodyRender({ body }: { body: string }) {
  const segs = parseMessageBody(body);
  return (
    <>
      {segs.map((seg, i) => {
        if (seg.kind === 'text') {
          return <Fragment key={i}>{seg.text}</Fragment>;
        }
        const internal =
          seg.href.startsWith('/') ||
          (typeof window !== 'undefined' && seg.href.startsWith(window.location.origin));
        return (
          <a
            key={i}
            href={seg.href}
            target={internal ? '_self' : '_blank'}
            rel={internal ? undefined : 'noopener noreferrer'}
            className="underline decoration-accent/60 underline-offset-2 hover:decoration-accent"
          >
            {seg.text}
          </a>
        );
      })}
    </>
  );
}
